"use client";

import { useEffect, useState, useRef, useCallback } from "react";
import Link from "next/link";
import { getSupabaseBrowserClient } from "@/lib/supabase-browser";
import { loadCampPriceUnitWitnesses } from "@/lib/camps/price-unit-witness";
import { formatPrice, observedPriceUnit } from "@/lib/price";
import { safeHttpUrl } from "@/lib/validation";

type SavedCamp = {
  name: string;
  slug: string;
  category_slug: string | null;
  price_cents: number | null;
  price_unit: import("@/lib/price").PriceUnit | null;
  // m526 (#2294) — the witness observedPriceUnit reads beside the unit. Fetched
  // by the tolerant helper (not the select above): naming the column in the main
  // select would fail the whole fetch until the migration is applied.
  price_unit_source: string | null;
  photo_url: string | null;
};

/**
 * SavedDrawerPeek — slides in from the right after a camp is saved.
 * Shows the just-saved camp card for 3 seconds, then auto-closes.
 * Tapping it navigates to /saved. Tapping outside closes it.
 *
 * Listens for `kamp:camp-saved` custom event with { campId, campName, action }.
 * Only shows on "save" actions, not "unsave".
 */
export function SavedDrawerPeek() {
  const [visible, setVisible] = useState(false);
  const [camp, setCamp] = useState<SavedCamp | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  const dismiss = useCallback(() => {
    setVisible(false);
    if (timerRef.current) clearTimeout(timerRef.current);
  }, []);

  useEffect(() => {
    async function handleSave(e: Event) {
      const detail = (e as CustomEvent).detail as {
        action: "save" | "unsave";
        campId?: string;
        campName?: string;
      } | undefined;

      if (!detail || detail.action !== "save" || !detail.campId) return;

      // Fetch camp details for the peek card
      try {
        const supabase = getSupabaseBrowserClient();
        const [{ data }, witnesses] = await Promise.all([
          supabase
            .from("camps")
            .select("name, slug, category_slug, price_cents, price_unit, photo_url")
            .eq("id", detail.campId)
            .maybeSingle(),
          // m526 — separate tolerant read; see SavedCamp.price_unit_source.
          loadCampPriceUnitWitnesses(supabase, [detail.campId]),
        ]);

        if (data) {
          setCamp({
            ...(data as Omit<SavedCamp, "price_unit_source">),
            price_unit_source: witnesses.get(detail.campId) ?? null,
          });
        } else {
          setCamp({
            name: detail.campName ?? "Camp",
            slug: "",
            category_slug: null,
            price_cents: null,
            price_unit: null,
            price_unit_source: null,
            photo_url: null,
          });
        }
      } catch {
        setCamp({
          name: detail.campName ?? "Camp",
          slug: "",
          category_slug: null,
          price_cents: null,
          price_unit: null,
          price_unit_source: null,
          photo_url: null,
        });
      }

      setVisible(true);

      // Auto-dismiss after 3.5s
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => setVisible(false), 3500);
    }

    window.addEventListener("kamp:camp-saved", handleSave);
    return () => {
      window.removeEventListener("kamp:camp-saved", handleSave);
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  if (!camp) return null;

  const priceLabel =
    camp.price_cents != null
      ? formatPrice(camp.price_cents, observedPriceUnit(camp))
      : null;
  const safePhotoUrl = safeHttpUrl(camp.photo_url);

  return (
    <div
      className={`fixed right-0 top-20 z-[250] w-72 max-w-[85vw] transition-transform duration-300 ease-out ${
        visible ? "translate-x-0" : "translate-x-full"
      }`}
    >
      <Link
        href="/saved"
        onClick={dismiss}
        className="block rounded-l-xl border border-r-0 border-wildroost-teal/20 bg-white p-4 shadow-lg"
      >
        <div className="mb-2 flex items-center gap-2">
          <span className="flex h-6 w-6 items-center justify-center rounded-full bg-wildroost-teal/10 text-xs text-wildroost-teal">
            &#x2713;
          </span>
          <span className="text-xs font-semibold uppercase tracking-wider text-wildroost-teal">
            Saved
          </span>
        </div>

        <div className="flex items-center gap-3">
          {safePhotoUrl ? (
            <img
              src={safePhotoUrl}
              alt=""
              className="h-12 w-12 rounded-lg object-cover"
            />
          ) : (
            <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-wildroost-sand text-lg text-wildroost-ink/30">
              &#x2605;
            </div>
          )}
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-semibold text-wildroost-ink">
              {camp.name}
            </p>
            <p className="text-xs text-wildroost-ink/50">
              {[camp.category_slug, priceLabel].filter(Boolean).join(" · ") ||
                "Added to your saved camps"}
            </p>
          </div>
        </div>

        <p className="mt-2 text-[11px] text-wildroost-ink/40">
          View all saved camps &rarr;
        </p>
      </Link>
    </div>
  );
}
